import * as React from "react";
import { createRef, useEffect, useState } from "react";
import { Loader } from "@skbkontur/react-ui";
import { Button } from "react-ui";
import { NetUploadIcon } from "@skbkontur/icons/NetUploadIcon";

import { EmptyTemplate, Header, UnitCaption } from "../../../Forms/InjectableModules/InjectableControls";
import { FileProps } from "../../../Engine/Core/ModalManager/Modals/Utils/Types";
import { AttachInfo } from "../../../../../Common/CandyApiClient/Types/Attachments/Attacments";
import { UploadedFileInfo } from "../../../Engine/ActionCreators/FufUploadActionCreator";
import { CustomFile } from "../../../Helpers/file";
import { FufSizeTooLargeError } from "../../../../../Common/CandyApiClient/Types/DraftActions/FufSizeTooLargeError";
import { ATTACHMENT_FORM_OPEN_EMPTY, ATTACHMENT_FORM_OPEN_FUF, ATTACHMENT_FORM_REMOVE } from "../../metricsAction";
import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import { AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { UploadFileOptions, UploadFormTypes } from "../../../Engine/Externals/FufManager/IFufManager";
import { useAddSingleUnit } from "../Unit/AddSingleUnitContext";
import { CurrencyHelper } from "../../Internals/ControlsReexport/CustomControls/CurrencyInput/CurrencyHelper";
import { SetUnitItemExtraInfo } from "../Unit/Types";

import { AttachmentFormUploadFile } from "./AttachmentFormUploadFile";
import {
    AttachmentFormActions,
    AttachmentFormActionText,
    AttachmentFormContent,
    AttachmentFormEmpty,
    AttachmentFormErrorText,
    AttachmentFormFileInfo,
    AttachmentFormFileNameText,
    AttachmentFormNdsSumText,
} from "./AttachmentForm.styles";

export type OpenAttachmentAction = "openDraft" | "uploadFile";

export interface AttachmentFormProps {
    onOpenAsDraft: () => Promise<void>;
    onLoadAttachmentFile: (loadedFileProps: FileProps) => Promise<void>;
    onRemoveAttachment: (bindingPath: AbsoluteResolvedModelPath) => Promise<void>;
    onOpenFufAsDraft: (attachInfo: AttachInfo) => Promise<void>;
    getFileProperties: (file?: CustomFile, options?: UploadFileOptions) => Promise<UploadedFileInfo>;
    attachmentInfo?: AttachInfo;
    caption: string;
    description: string;
    requestedGfv: string;
    currentGfv: string;
    isFilled: boolean;
    bindingPath: AbsoluteResolvedModelPath;
    addPage?: () => void;
    disabled?: boolean;
    openAttachmentAction?: OpenAttachmentAction | null;
    setOpenAttachmentAction?: (openAttachmentAction: OpenAttachmentAction | null) => void;
    isFormInFormNds: boolean;
    setUnitItemExtraInfo: SetUnitItemExtraInfo;
    getPortalUserId: () => Promise<string>;
    emptyUnitTitle?: string;
    emptyUnitDescription?: React.ReactNode;
}

export function AttachmentForm(props: AttachmentFormProps): JSX.Element {
    const track = useMetrics();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const addPage = useAddSingleUnit();

    const openBtnRef = createRef<HTMLElement>();
    const uploadBtnRef = createRef<HTMLElement>();
    const [isFileLoading, setIsFileLoading] = useState<boolean>(false);

    const attachmentInfo = props.attachmentInfo;
    const totalNdsSum = attachmentInfo?.meta?.ndsTotalSum;

    useEffect(() => {
        props.setUnitItemExtraInfo(totalNdsSum?.toString());
    }, [totalNdsSum]);

    useEffect(() => {
        if (props.openAttachmentAction === "openDraft") {
            openBtnRef.current?.click();
            props.setOpenAttachmentAction?.(null);
        } else if (props.openAttachmentAction === "uploadFile") {
            uploadBtnRef.current?.click();
            props.setOpenAttachmentAction?.(null);
        }
    }, [props.openAttachmentAction]);

    const openAsDraft = async () => {
        setIsLoading(true);

        if (!props.isFilled && props.setOpenAttachmentAction) {
            addPage();
            props.setOpenAttachmentAction("openDraft");
            return;
        }

        track(ATTACHMENT_FORM_OPEN_EMPTY, { attachmentId: props.attachmentInfo?.id, gfv: props.requestedGfv });
        await props.onOpenAsDraft();
    };

    const fakeUploadFile = () => {
        if (!props.isFilled && props.setOpenAttachmentAction) {
            addPage();
            props.setOpenAttachmentAction("uploadFile");
        }
    };

    const openFufAsDraft = async (uploadAttachmentInfo: AttachInfo) => {
        setIsLoading(true);
        try {
            await props.onOpenFufAsDraft(uploadAttachmentInfo);
            track(ATTACHMENT_FORM_OPEN_FUF, { attachmentId: props.attachmentInfo?.id, gfv: props.requestedGfv });
        } catch (e) {
            let errorText = "Произошла ошибка при открытии файла";
            if (e instanceof FufSizeTooLargeError) {
                errorText = "Файл недоступен для редактирования: размер файла превышает ограничение";
            }
            track(ATTACHMENT_FORM_OPEN_FUF, {
                attachmentId: props.attachmentInfo?.id,
                gfv: props.requestedGfv,
                errorText: errorText,
            });
            setError(errorText);
        }
        setIsLoading(false);
    };

    const removeAttachment = async () => {
        setIsLoading(true);
        setError(undefined);
        track(ATTACHMENT_FORM_REMOVE, { attachmentId: props.attachmentInfo?.id, gfv: props.requestedGfv });
        await props.onRemoveAttachment(props.bindingPath);
        setIsLoading(false);
    };

    const prepareIfNeedAndGetFileProperties = async (uploadFile?: CustomFile): Promise<UploadedFileInfo> => {
        try {
            setIsFileLoading(true);
            const result = await props.getFileProperties(uploadFile, {
                formType: props.isFormInFormNds ? UploadFormTypes.NDS : UploadFormTypes.default,
                declarationGfv: props.currentGfv,
                requestedGfv: props.requestedGfv,
            });

            return result;
        } finally {
            setIsFileLoading(false);
        }
    };

    const onLoadAttachmentFile: typeof props.onLoadAttachmentFile = async loadedFileProps => {
        try {
            setIsFileLoading(true);
            await props.onLoadAttachmentFile(loadedFileProps);
        } finally {
            setIsFileLoading(false);
        }
    };

    const getPageContent = () => {
        if (attachmentInfo?.contentUri !== undefined) {
            return (
                <>
                    <AttachmentFormFileInfo>
                        <AttachmentFormFileNameText>
                            {attachmentInfo.meta.fileName ?? attachmentInfo.meta.fileNamePatched}
                        </AttachmentFormFileNameText>
                        {attachmentInfo.meta.ndsTotalSum && (
                            <AttachmentFormNdsSumText>
                                Общая сумма налога:{" "}
                                {CurrencyHelper.format(attachmentInfo.meta.ndsTotalSum.toString(), {
                                    fractionDigits: 2,
                                })}
                                {"\u00A0"}₽
                            </AttachmentFormNdsSumText>
                        )}
                    </AttachmentFormFileInfo>
                    {!props.disabled && (
                        <AttachmentFormActionText>
                            {error === undefined ? (
                                <Button
                                    data-tid="openLoadedAttachmentEditor"
                                    onClick={() => openFufAsDraft(attachmentInfo)}>
                                    Редактировать
                                </Button>
                            ) : (
                                <AttachmentFormErrorText>{error}</AttachmentFormErrorText>
                            )}
                        </AttachmentFormActionText>
                    )}
                    <AttachmentFormActionText>
                        <Button data-tid="deleteLoadedFile" onClick={removeAttachment}>
                            Удалить загруженный файл
                        </Button>
                    </AttachmentFormActionText>
                </>
            );
        }
        return (
            <Loader active={isLoading} caption={null} data-tid={"attachmentFormEmpty"}>
                <EmptyTemplate
                    title={props.emptyUnitTitle ?? "Раздел не заполнен"}
                    description={props.emptyUnitDescription}>
                    <AttachmentFormActions>
                        {!props.disabled && !isFileLoading && (
                            <AttachmentFormActionText>
                                <Button data-tid="fillInSystem" onClick={openAsDraft}>
                                    Заполнить
                                    <span ref={openBtnRef}></span>
                                </Button>
                            </AttachmentFormActionText>
                        )}
                        <AttachmentFormActionText>
                            {props.isFilled && props.setOpenAttachmentAction ? (
                                <AttachmentFormUploadFile
                                    onLoadAttachmentFile={onLoadAttachmentFile}
                                    requestedGfv={props.requestedGfv}
                                    prepareIfNeedAndGetFileProperties={prepareIfNeedAndGetFileProperties}
                                    ref={uploadBtnRef}
                                    getPortalUserId={props.getPortalUserId}
                                />
                            ) : (
                                <Button data-tid="AddFiles" icon={<NetUploadIcon size={14} />} onClick={fakeUploadFile}>
                                    Добавить файл
                                </Button>
                            )}
                        </AttachmentFormActionText>
                    </AttachmentFormActions>
                </EmptyTemplate>
            </Loader>
        );
    };

    return (
        <AttachmentFormEmpty isFilled={props.isFilled}>
            <UnitCaption>{props.caption}</UnitCaption>
            <Header>
                <span>{props.description}</span>
            </Header>
            <AttachmentFormContent>{getPageContent()}</AttachmentFormContent>
        </AttachmentFormEmpty>
    );
}
